import React, { useState } from 'react';
import { BaseControlProps } from '../../models/BaseControlProps';
import { Box, Button, CircularProgress, Divider, FormControl, FormLabel, TextField, Typography } from '@mui/material';

import { AuthUserEmailChangeRequest } from '../../models/AuthUserEmailChangeRequest';
import { UserAPIService } from '../../services/UserAPIService';
import { useAuth } from '../../services/AuthContext';
import { useMessage } from '../../components/Common/MessageContext/MessageContext';
import { UtilService } from '../../services/UtilService';
import ConfirmPrompt from '../../components/Common/ConfirmPrompt/ConfirmPrompt';


const ChangeEmail = (props: BaseControlProps) => {
    const [scope, apply] = React.useState<AuthUserEmailChangeRequest>({
        oldEmail: '',
        newEmail: ''
    });
    const authService = new UserAPIService();
    const authContext = useAuth();
    const [loading, setLoading] = useState(false);
    const { showMessage } = useMessage();
    const [inputValid, isInputValid] = React.useState(false);
    const [newEmailError, setNewEmailError] = React.useState(false);
    const [newEmailErrorMessage, setNewEmailErrorMessage] = React.useState('');
    const [confirmVisible, setConfirmVisible] = useState(false);
    const [confirmTitle, setConfirmTitle] = useState('');
    const [confirmMessage, setConfirmMessage] = useState('');
    const [confirmLoading, setConfirmLoading] = useState(false);

    function applyScope(){
        apply(UtilService.cloneObject(scope));
    }

    const changeEmail = () => {
        
        if (!inputValid) { return; }

        setLoading(true);
        scope.oldEmail = authContext.emailAddress;
        applyScope();
        authService.changeEmail(scope, authContext.token).then((response) => {
            if (response.error) {
                showMessage(response.error, 'error');
            }
            else {
                if (response.response?.emailSent) {
                    apply({ newEmail: '', oldEmail: ''});
                    showMessage("Email changes successfully! verify email and login again to continue.", "success");
                    setTimeout(() => {
                        authContext.logout();
                    }, 3000)
                }
                else {
                    showMessage("We are sorry, but we are not able to change you email, please contact Mercer", 'error');
                }
            }

            setLoading(false);
            setConfirmLoading(false);
            setConfirmVisible(false);
        })
    }

    const handleConfirmCancel = () => {
        setConfirmVisible(false);
    }

    const handleConfirmConfirmed = async () => {
        setConfirmLoading(true);

        await changeEmail();
    }

    const renderConfirmPrompt = () => {
        return (
            <ConfirmPrompt 
                open={confirmVisible}
                title={confirmTitle}
                message={confirmMessage}
                onConfirm={handleConfirmConfirmed}
                onCancel={handleConfirmCancel}
                isLoading={confirmLoading}
            />
        );
    }

    const validateInputs = () => {
        const newEmail = document.getElementById('newEmail') as HTMLInputElement;
        scope.newEmail = newEmail.value;
        applyScope();
        let isValid = true;

        if (!newEmail.value || !/\S+@\S+\.\S+/.test(newEmail.value)) {
            setNewEmailError(true);
            setNewEmailErrorMessage('Please enter a valid email address.');
            isValid = false;
        } else {
            setNewEmailError(false);
            setNewEmailErrorMessage('');
        }
        
        if (!isValid) {
            isInputValid(false);
        } else {
            isInputValid(true);
        }

        return isValid;
    };

    return (
        <>
            <Box sx={{ width: '100%', pb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography component="h2" variant="h5" gutterBottom>
                        Change Email
                    </Typography>
                </Box>
                <Divider />
            </Box>
            <Box
                 sx={{ 
                    maxWidth: 600, // Maximum width of the form
                    margin: '20 auto', // Center the form horizontally
                    padding: 2, // Padding around the form
                    border: '1px solid #ddd', // Optional border for better visibility
                    borderRadius: 1 // Optional rounded corners
                }}
            >
                <Box 
                    component="form"
                    onSubmit={(e) => {
                        e.preventDefault();
                        setConfirmTitle('Change Email');
                        const msg = "Are you sure you want to change your email address ?";
                        setConfirmMessage(msg);
                        setConfirmVisible(true)
                    }}
                >
                    <FormControl fullWidth sx={{ mb: 3 }}>
                        <FormLabel id="change-email" htmlFor='newEmail'>Email : </FormLabel>
                        <TextField 
                            required
                            fullWidth
                            id="newEmail"
                            name="newEmail"
                            type="text"
                            variant='outlined'
                            value={scope.newEmail} 
                            onChange={(e)=>{
                                scope.newEmail = e.currentTarget.value;
                                applyScope();
                            }}
                            error={newEmailError}
                            helperText={newEmailErrorMessage}
                            color={newEmailError ? 'error' : 'primary' }
                        />
                    </FormControl>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ marginLeft: 'auto' }}>
                            <Button 
                                type='submit'
                                variant="contained" 
                                fullWidth
                                disabled={loading} 
                                onClick={validateInputs}
                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                                sx={{
                                    '&.Mui-disabled': {
                                        color: (theme) => theme.palette.primary.contrastText,
                                    }
                                }}
                            >
                                {loading ? 'Loading...' : 'Change Email'}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {renderConfirmPrompt()}
        </>
    )
}

export default ChangeEmail;